import { Button } from '@src/shared/ui/components/Button';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { isEmpty } from 'lodash';
import React, { useMemo } from 'react';

/**
 * Filters
 *
 * Provides form fields to control the data returned from the api.
 *
 * @param assets
 * @param form
 * @param isSubmitting
 * @returns {Element}
 * @constructor
 */
export function Filters({ assets, form, isSubmitting }) {
  const formValues = form?.watch() || {};

  /**
   * sourceInput
   *
   * - Provides source options for the select input, displaying both the full source name and code ID.
   * - Enables filtering by full source name or code ID using fuzzy search.
   */
  const sourceInput = useMemo(() => {
    const sources = assets?.ListEimpSources?.data || [];
    const lobSections = {};

    sources.forEach(({ codeDesc, codeId }) => {
      const codeDescription = codeDesc.split(';');
      const fullSourceName = codeDescription?.[0].trim();

      let lob = codeDescription?.[1]?.replace(/lob:\s*/i, '').trim();

      if (!lob) {
        lob = 'Other';
      }

      const keywords = new Set([codeId, ...fullSourceName.split(/\s+/)]);

      const option = {
        label: `${fullSourceName} (${codeId})`,
        tags: [...keywords],
        value: codeId,
      };

      if (!lobSections[lob]) {
        lobSections[lob] = [];
      }

      lobSections[lob].push(option);
    });

    return Object.entries(lobSections)
      .sort(([a], [b]) => {
        return a.localeCompare(b);
      })
      .map(([lob, items]) => {
        return {
          items: items.sort((a, b) => {
            return a.label.localeCompare(b.label);
          }),
          section: lob,
        };
      });
  }, [assets?.ListEimpSources?.data]);

  return (
    <ErrorHandler location="src/pages/private/Dashboards/SourceReportsDashboard/components/Filters/Filters.jsx">
      <Grid>
        <Grid.Col span={{ lg: 2, md: 3, sm: 4, xl: 2, xs: 12 }}>
          <SelectInput
            label="Attribute"
            model="attribute"
            options={[
              { label: 'SSN', value: 'SSN' },
              { label: 'DOB', value: 'DOB' },
            ]}
            placeholder="Select Attribute --"
          />
        </Grid.Col>
        <Grid.Col span={{ lg: 4, md: 6, sm: 6, xl: 3, xs: 12 }}>
          <SelectInput
            isClearable
            isDisabled={isEmpty(formValues?.attribute)}
            isSearchable
            keys={['label', 'tags']}
            label="Source"
            maxListHeight={300}
            model="source"
            options={sourceInput}
            placeholder="Select Source --"
          />
        </Grid.Col>
        <Grid.Col css={{ alignContent: 'flex-end' }} span={{ lg: 6, md: 3, sm: 2, xl: 7, xs: 12 }}>
          <Button
            isDisabled={isEmpty(formValues?.attribute) || isEmpty(formValues?.source) || isSubmitting}
            type="submit"
          >
            Apply
          </Button>
        </Grid.Col>
      </Grid>
    </ErrorHandler>
  );
}
